import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import WeeklyDigest from "../components/WeeklyDigest";
import { useNavigate } from "react-router-dom";
import { Activity, AlertTriangle, Package, ShieldCheck, TrendingUp, Sun, Wrench, ChevronDown, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

// Wrapper that lets Super Admin / Plant Head collapse the Weekly Digest card.
// State is persisted in localStorage so the choice sticks across reloads.
function CollapsibleDigest() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem("digest_card_open") !== "0"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("digest_card_open", open ? "1" : "0"); } catch { /* ignore */ }
  }, [open]);
  return (
    <div className="bg-white border border-[#EEEEEE] rounded-sm" data-testid="digest-collapsible">
      <button onClick={() => setOpen((v) => !v)}
        data-testid="digest-collapse-toggle"
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left border-b border-[#EEEEEE] hover:bg-[#F5F5F5]">
        {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0D2B5E]">Weekly Digest</span>
        <span className="ml-auto text-[10px] tracking-widest uppercase text-[#0D2B5E]/50">{open ? "Hide" : "Show"}</span>
      </button>
      {open && <div className="p-2"><WeeklyDigest mode="card"/></div>}
    </div>
  );
}

const PIPELINE = [
  { key: "pending_grn", label: "Pending GRN", color: "#E65100", to: "/gate-entry" },
  { key: "pending_quality", label: "Pending Quality", color: "#1565C0", to: "/grn" },
  { key: "quality_pending", label: "Quality Open", color: "#1565C0", to: "/quality" },
  { key: "pending_deviation", label: "Deviation Open", color: "#C62828", to: "/quality" },
  { key: "stock_in", label: "In Stock", color: "#2E7D32", to: "/stock" },
];

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [stock, setStock] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [myDay, setMyDay] = useState(null);
  const [showDigestModal, setShowDigestModal] = useState(false);
  // Wave-G: calibration watch — fetched from /api/assets and filtered
  // client-side on the server-computed `calibration_flag` field.
  const [calAssets, setCalAssets] = useState([]);
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.role?.is_super_admin || (user?.role?.name === "Plant Head/Admin");

  // Monday auto-popup: open the digest modal once per ISO week, only on Mondays.
  // NOTE — localStorage here stores a NON-SENSITIVE UI flag (`digest_seen_…`)
  // that simply marks this browser as having seen the digest popup this week.
  // It is NOT a token / credential / session marker. Auth uses httpOnly
  // cookies (`api.lib.js` → axios `withCredentials`). Suppressing the
  // "sensitive data in localStorage" code-review warning intentionally.
  useEffect(() => {
    if (!isAdmin) return;
    const today = new Date();
    if (today.getDay() !== 1) return;  // 1 = Monday
    const weekKey = `digest_seen_${today.getFullYear()}_${getISOWeek(today)}`;
    if (!localStorage.getItem(weekKey)) {
      setShowDigestModal(true);
      // value is just a presence marker — content is ignored on read
      localStorage.setItem(weekKey, "1");
    }
  }, [isAdmin]);

  const [lastRefresh, setLastRefresh] = useState(null);

  // Wave B (Feb-2026) — Real-time dashboard sync.
  // We combine three refresh triggers so widgets are always current:
  //   1. Initial fetch on mount.
  //   2. Polling every 20s while the tab is active (cheap; endpoints are
  //      already aggregated & indexed).
  //   3. Instant refresh on any successful POST/PUT/DELETE anywhere in the
  //      app — the axios response interceptor in lib/api.js fires a
  //      "supplar:mutation" CustomEvent, and we listen for it here. This
  //      catches GRN creation/approval, PO creation, MR issue/receipt,
  //      inventory adjustment, dispatch, and stock transfers WITHOUT
  //      needing to enumerate individual endpoints.
  //   4. Refetch when the browser tab regains focus (kiosk-friendly).
  const refresh = React.useCallback(() => {
    return Promise.all([
      api.get("/dashboard/summary"),
      api.get("/inventory/stock-summary"),
      api.get("/alerts"),
      api.get("/my-day"),
      api.get("/assets", { params: { include_scrapped: false } }).catch(() => ({ data: [] })),
    ]).then(([s, ss, al, md, ax]) => {
      setSummary(s.data); setStock(ss.data); setAlerts(al.data); setMyDay(md.data);
      setCalAssets((ax.data || []).filter(
        (a) => a.calibration_flag === "due_soon" || a.calibration_flag === "overdue",
      ));
      setLastRefresh(new Date());
    }).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    // Poll every 20 seconds
    const pollId = setInterval(refresh, 20000);
    // Listen for mutation events fired by the axios interceptor
    const onMutation = () => refresh();
    window.addEventListener("supplar:mutation", onMutation);
    // Refresh when tab regains focus
    const onVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(pollId);
      window.removeEventListener("supplar:mutation", onMutation);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  const q = summary?.quality_summary || { ok: 0, ng: 0, hold: 0, pending: 0 };
  const qTotal = (q.ok + q.ng + q.hold + q.pending) || 1;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle="Live control room — warehouse operations at a glance"
        right={
          <div className="flex items-center gap-3">
            {/* Wave B (Feb-2026) — live indicator */}
            {lastRefresh && (
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-[#2E7D32]" data-testid="dashboard-live-indicator">
                <span className="w-2 h-2 rounded-full bg-[#2E7D32] animate-pulse"/>
                LIVE · updated {Math.max(0, Math.floor((Date.now() - lastRefresh.getTime()) / 1000))}s ago
              </div>
            )}
            {isAdmin && (
              <button onClick={() => setShowDigestModal(true)} data-testid="open-digest-btn"
                className="text-xs uppercase tracking-widest font-bold text-[#1565C0] border border-[#1565C0]/30 hover:bg-[#1565C0] hover:text-white px-3 py-1.5 rounded-sm transition">
                Last week ↗
              </button>
            )}
          </div>
        }
      />
      <div className="p-8 space-y-6">
        {showDigestModal && <WeeklyDigest mode="modal" onClose={() => setShowDigestModal(false)}/>}
        {isAdmin && <CollapsibleDigest/>}
        {/* My Day — role-aware morning briefing */}
        {myDay && myDay.tiles?.length > 0 && (
          <div className="bg-gradient-to-br from-[#0D2B5E] to-[#1565C0] text-white rounded-sm p-6 shadow-md" data-testid="my-day-widget">
            <div className="flex items-center gap-2 mb-4">
              <Sun size={18} className="text-[#FFB74D]"/>
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold">My Day · {myDay.role}</div>
              <div className="text-xs text-white/60 ml-auto">Hi, {user?.name}</div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {myDay.tiles.map((t) => {
                const toneColor = t.tone === "red" ? "#FF5252" : t.tone === "amber" ? "#FFB74D" : "#4FC3F7";
                const toneRing = t.tone === "red" ? "ring-[#FF5252]/40 bg-[#FF5252]/20" : t.tone === "amber" ? "ring-[#FFB74D]/40 bg-[#FFB74D]/20" : "ring-[#4FC3F7]/40 bg-[#4FC3F7]/20";
                return (
                  <button key={t.key} onClick={() => nav(t.link)} data-testid={`my-day-${t.key}`}
                    className={`relative rounded-sm p-3 text-left border-l-4 transition hover:bg-white/15 bg-white/10 border-white/10`}
                    style={{ borderLeftColor: toneColor }}
                  >
                    <div className={`absolute top-2 right-2 w-2.5 h-2.5 rounded-full ring-4 ${toneRing}`} style={{ backgroundColor: toneColor }} data-testid={`my-day-tone-${t.key}`} aria-label={`${t.tone} priority`} />
                    <div className="text-2xl font-bold font-mono mb-1" style={{ color: toneColor }}>{t.value}</div>
                    <div className="text-xs text-white/85 leading-tight">{t.label}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Top KPI row */}
        <div className="grid grid-cols-4 gap-4">
          <KpiCard icon={Activity} label="Active Alerts" value={alerts.length} color="#C62828" testId="kpi-alerts" onClick={() => nav("/alerts")} />
          <KpiCard icon={Package} label="Stock SKUs" value={stock.length} color="#1565C0" testId="kpi-skus" />
          <KpiCard icon={ShieldCheck} label="Quality OK (total)" value={q.ok} color="#2E7D32" testId="kpi-ok" />
          <KpiCard icon={TrendingUp} label="Issued Items (4w)" value={(summary?.weekly_issues || []).reduce((a, x) => a + x.qty, 0)} color="#0D2B5E" testId="kpi-issued" />
        </div>

        {/* Pipeline Kanban */}
        <div className="bg-white border border-[#EEEEEE] rounded-sm">
          <div className="px-5 py-3 border-b border-[#EEEEEE] flex items-center justify-between">
            <div className="text-[10px] tracking-[0.2em] font-bold text-[#0D2B5E] uppercase">GRN Pipeline</div>
            <div className="text-xs text-[#0D2B5E]/50 font-mono">LIVE</div>
          </div>
          <div className="grid grid-cols-5 gap-0 grid-lines">
            {PIPELINE.map((p) => (
              <button key={p.key}
                onClick={() => nav(p.to)}
                data-testid={`pipeline-${p.key}`}
                className="p-5 text-left hover:bg-[#F5F5F5] transition-colors"
              >
                <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60">{p.label}</div>
                <div className="text-4xl font-bold font-mono mt-2" style={{ color: p.color }}>
                  {summary?.pipeline?.[p.key] ?? "—"}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Two col: Stock summary + Alerts */}
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white border border-[#EEEEEE] rounded-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-[#EEEEEE] flex items-center justify-between">
              <div className="text-[10px] tracking-[0.2em] font-bold text-[#0D2B5E] uppercase">Live Stock Summary</div>
              <button onClick={() => nav("/stock")} className="text-[10px] text-[#1565C0] uppercase tracking-widest font-bold" data-testid="view-stock-btn">View all →</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                  <tr>
                    <th className="text-left px-4 py-2">Code</th>
                    <th className="text-left px-4 py-2">Material</th>
                    <th className="text-right px-4 py-2">Qty</th>
                    <th className="text-right px-4 py-2">Min / Max</th>
                    <th className="text-left px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stock.slice(0, 10).map((s, idx) => (
                    <tr key={s.material_id} className={idx % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                      <td className="px-4 py-2 font-mono text-[#1565C0]">{s.code}</td>
                      <td className="px-4 py-2">{s.name}</td>
                      <td className="px-4 py-2 font-mono text-right">{s.qty}</td>
                      <td className="px-4 py-2 font-mono text-right text-[#0D2B5E]/60">{s.min_stock} / {s.max_stock}</td>
                      <td className="px-4 py-2"><StatusBadge status={s.status} /></td>
                    </tr>
                  ))}
                  {stock.length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-sm text-[#0D2B5E]/50">No stock data yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-[#EEEEEE] rounded-sm">
            <div className="px-5 py-3 border-b border-[#EEEEEE]">
              <div className="text-[10px] tracking-[0.2em] font-bold text-[#0D2B5E] uppercase">Quality</div>
            </div>
            <div className="p-6">
              <QualityDonut q={q} total={qTotal} />
              <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                <LegendRow color="#2E7D32" label="OK" value={q.ok} />
                <LegendRow color="#C62828" label="NG" value={q.ng} />
                <LegendRow color="#E65100" label="Hold" value={q.hold} />
                <LegendRow color="#1565C0" label="Pending" value={q.pending} />
              </div>
            </div>
          </div>
        </div>

        {/* FIFO aging + Alerts */}
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white border border-[#EEEEEE] rounded-sm">
            <div className="px-5 py-3 border-b border-[#EEEEEE]">
              <div className="text-[10px] tracking-[0.2em] font-bold text-[#0D2B5E] uppercase">FIFO Aging (Oldest First)</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                  <tr>
                    <th className="text-left px-4 py-2">GRN</th>
                    <th className="text-left px-4 py-2">Lot No.</th>
                    <th className="text-left px-4 py-2">Material</th>
                    <th className="text-right px-4 py-2">Qty</th>
                    <th className="text-right px-4 py-2">Age (days)</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary?.fifo_aging || []).map((a, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                      <td className="px-4 py-2 font-mono text-[#1565C0]">{a.grn_number}</td>
                      <td className="px-4 py-2 font-mono">{a.lot_no}</td>
                      <td className="px-4 py-2">{a.material_name}</td>
                      <td className="px-4 py-2 font-mono text-right">{a.qty}</td>
                      <td className={`px-4 py-2 font-mono text-right ${a.age_days > 60 ? "fifo-critical" : a.age_days > 30 ? "fifo-old" : ""}`}>{a.age_days}</td>
                    </tr>
                  ))}
                  {(!summary?.fifo_aging || summary.fifo_aging.length === 0) && (
                    <tr><td colSpan={5} className="p-6 text-center text-sm text-[#0D2B5E]/50">No stock in warehouse.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-white border border-[#EEEEEE] rounded-sm">
            <div className="px-5 py-3 border-b border-[#EEEEEE] flex items-center justify-between">
              <div className="text-[10px] tracking-[0.2em] font-bold text-[#0D2B5E] uppercase flex items-center gap-2">
                <AlertTriangle size={12} /> Alerts ({alerts.length})
              </div>
              <button onClick={() => nav("/alerts")} className="text-[10px] text-[#1565C0] uppercase tracking-widest font-bold" data-testid="view-alerts-btn">View all →</button>
            </div>
            <div className="divide-y divide-[#EEEEEE] max-h-80 overflow-y-auto">
              {alerts.length === 0 && <div className="p-6 text-center text-sm text-[#0D2B5E]/50">All clear.</div>}
              {alerts.slice(0, 8).map((a, i) => (
                <div key={i} className="px-5 py-3 flex items-start gap-3" data-testid={`alert-${a.type}-${i}`}>
                  <div className="w-2 h-2 rounded-full mt-1.5" style={{ backgroundColor: a.level === "red" ? "#C62828" : "#E65100" }} />
                  <div className="flex-1 text-xs">
                    <div className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/50 mb-0.5">{a.type.replace(/_/g, " ")}</div>
                    <div className="text-[#0D2B5E]">{a.message}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* GRN Kanban + NG Trend mini */}
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white border border-[#EEEEEE] rounded-sm" data-testid="grn-kanban-widget">
            <div className="px-5 py-3 border-b border-[#EEEEEE] flex items-center justify-between">
              <div className="text-[10px] tracking-[0.2em] font-bold text-[#0D2B5E] uppercase">GRN Kanban — pipeline live view</div>
              <button onClick={() => nav("/grn")} className="text-[10px] text-[#1565C0] uppercase tracking-widest font-bold">Open GRN →</button>
            </div>
            <div className="grid grid-cols-5 gap-0 grid-lines">
              {PIPELINE.map((p) => {
                const lst = (summary?.grn_kanban?.[p.key]) || [];
                return (
                  <div key={p.key} className="border-r border-[#EEEEEE] last:border-r-0 max-h-72 overflow-y-auto" data-testid={`kanban-col-${p.key}`}>
                    <div className="px-3 py-2 sticky top-0 bg-[#F5F5F5] border-b border-[#EEEEEE] flex items-center justify-between">
                      <div className="text-[9px] tracking-widest uppercase font-bold" style={{ color: p.color }}>{p.label}</div>
                      <div className="text-[9px] font-mono text-[#0D2B5E]/50 bg-white px-1.5 py-0.5 rounded">{lst.length}</div>
                    </div>
                    <div className="divide-y divide-[#EEEEEE]">
                      {lst.length === 0 && <div className="p-3 text-[10px] text-[#0D2B5E]/40 italic">empty</div>}
                      {lst.slice(0, 6).map((c, i) => (
                        <button key={i} onClick={() => nav(p.to)} className="w-full text-left px-3 py-2 hover:bg-[#1565C0]/5 transition" data-testid={`kanban-card-${p.key}-${i}`}>
                          <div className="font-mono text-xs text-[#1565C0] font-bold">{c.ref}</div>
                          <div className="text-[10px] text-[#0D2B5E]/70 truncate">{c.supplier}</div>
                          <div className="text-[9px] text-[#0D2B5E]/40 font-mono">{c.items} item{c.items === 1 ? "" : "s"} · {(c.ts || "").slice(5, 10)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="bg-white border border-[#EEEEEE] rounded-sm" data-testid="ng-trend-widget">
            <div className="px-5 py-3 border-b border-[#EEEEEE] flex items-center justify-between">
              <div className="text-[10px] tracking-[0.2em] font-bold text-[#C62828] uppercase">NG Trend · Top 5 (30 days)</div>
              <button onClick={() => nav("/reports/ng-trend")} className="text-[10px] text-[#1565C0] uppercase tracking-widest font-bold" data-testid="view-ng-trend-btn">Full report →</button>
            </div>
            <div className="divide-y divide-[#EEEEEE] max-h-80 overflow-y-auto">
              {(summary?.ng_trend_top5 || []).length === 0 && <div className="p-6 text-center text-sm text-[#2E7D32]">No NG events in last 30 days ✓</div>}
              {(summary?.ng_trend_top5 || []).map((r, i) => (
                <div key={i} className="px-5 py-3" data-testid={`ng-mini-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-xs">
                      <div className="font-mono text-[#1565C0] font-bold">{r.supplier_code}</div>
                      <div className="text-[#0D2B5E]/70 truncate text-[10px]">{r.supplier_name}</div>
                    </div>
                    <div className="font-mono font-bold text-[#C62828] text-lg">{r.ng_count}</div>
                  </div>
                  <div className="text-[10px] text-[#0D2B5E]/50 mt-1 truncate">{r.material_code} · {r.top_failing_params || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Calibration watch — Wave-G. Surfaces assets with calibration_flag
            ∈ {due_soon (<30d), overdue (<0d)}. Hidden entirely if there's
            nothing to act on, so the dashboard stays clean for plants
            without calibrated equipment. */}
        {calAssets.length > 0 && (
          <div className="bg-white border border-[#EEEEEE] rounded-sm" data-testid="calibration-watch-widget">
            <div className="px-5 py-3 border-b border-[#EEEEEE] flex items-center justify-between bg-[#FFF3E0]">
              <div className="text-[10px] tracking-[0.2em] font-bold text-[#E65100] uppercase flex items-center gap-2">
                <Wrench size={12}/> Calibration Watch · {calAssets.length} asset{calAssets.length === 1 ? "" : "s"}
                {calAssets.some((a) => a.calibration_flag === "overdue") && (
                  <span className="ml-2 bg-[#C62828] text-white text-[9px] px-1.5 py-0.5 rounded-sm">
                    {calAssets.filter((a) => a.calibration_flag === "overdue").length} OVERDUE
                  </span>
                )}
              </div>
              <button onClick={() => nav("/assets")} className="text-[10px] text-[#1565C0] uppercase tracking-widest font-bold" data-testid="view-assets-btn">Open Asset Register →</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                  <tr>
                    <th className="text-left px-4 py-2">Asset #</th>
                    <th className="text-left px-4 py-2">Material / Description</th>
                    <th className="text-left px-4 py-2">Location</th>
                    <th className="text-left px-4 py-2">Due Date</th>
                    <th className="text-left px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {calAssets
                    .slice()
                    .sort((a, b) => (a.calibration_due_date || "").localeCompare(b.calibration_due_date || ""))
                    .slice(0, 10)
                    .map((a) => (
                      <tr key={a.id} className="border-t border-[#EEEEEE] hover:bg-[#FFF3E0]/40 cursor-pointer"
                        onClick={() => nav("/assets")} data-testid={`cal-row-${a.asset_number}`}>
                        <td className="px-4 py-2 font-mono font-bold text-[#0D2B5E]">{a.asset_number}</td>
                        <td className="px-4 py-2 text-xs">{a.material_code ? `${a.material_code} — ${a.material_name}` : (a.description || "—")}</td>
                        <td className="px-4 py-2 text-xs">{a.location}</td>
                        <td className="px-4 py-2 font-mono text-xs">{a.calibration_due_date || "—"}</td>
                        <td className="px-4 py-2">
                          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm text-white ${a.calibration_flag === "overdue" ? "bg-[#C62828]" : "bg-[#E65100]"}`}>
                            {a.calibration_flag === "overdue" ? "OVERDUE" : "DUE SOON"}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {calAssets.length > 10 && (
                <div className="px-5 py-2 text-[10px] text-[#0D2B5E]/50 italic border-t border-[#EEEEEE]">
                  Showing 10 of {calAssets.length} — open Asset Register for the full list.
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, testId, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="bg-white border border-[#EEEEEE] rounded-sm p-5 text-left hover:border-[#1565C0] transition-colors"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0D2B5E]/60">{label}</div>
          <div className="text-3xl font-bold font-mono mt-2" style={{ color }}>{value}</div>
        </div>
        <div className="p-2 rounded-sm" style={{ backgroundColor: `${color}1A`, color }}>
          <Icon size={18} />
        </div>
      </div>
    </button>
  );
}

function QualityDonut({ q, total }) {
  const values = [
    { k: "ok", v: q.ok, c: "#2E7D32" },
    { k: "ng", v: q.ng, c: "#C62828" },
    { k: "hold", v: q.hold, c: "#E65100" },
    { k: "pending", v: q.pending, c: "#1565C0" },
  ];
  const r = 50, C = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg viewBox="0 0 140 140" className="w-48 h-48 mx-auto">
      <circle cx="70" cy="70" r={r} fill="none" stroke="#EEEEEE" strokeWidth="18" />
      {values.map((s) => {
        const len = (s.v / total) * C;
        const dash = `${len} ${C - len}`;
        const offset = C * 0.25 - acc;
        acc += len;
        return s.v > 0 ? (
          <circle key={s.k} cx="70" cy="70" r={r} fill="none" stroke={s.c} strokeWidth="18"
            strokeDasharray={dash} strokeDashoffset={offset} transform="rotate(0 70 70)" />
        ) : null;
      })}
      <text x="70" y="66" textAnchor="middle" fontSize="22" fontWeight="700" fill="#0D2B5E" fontFamily="IBM Plex Mono">{total}</text>
      <text x="70" y="82" textAnchor="middle" fontSize="9" fill="#0D2B5E" opacity="0.6" letterSpacing="2">INSPECTIONS</text>
    </svg>
  );
}

function LegendRow({ color, label, value }) {
  return (
    <div className="flex items-center justify-between px-2 py-1">
      <div className="flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
        <span className="text-[#0D2B5E]/70 uppercase tracking-widest text-[10px]">{label}</span>
      </div>
      <span className="font-mono text-[#0D2B5E]">{value}</span>
    </div>
  );
}

// ISO 8601 week number (Mon = first day of week). Used for once-per-week popup gating.
function getISOWeek(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
}
